import { atom, useAtom, useSetAtom } from 'jotai'
import type { Getter, Setter } from 'jotai'
import type { ModalState, ModalContexts, ModalId, ModalRequiresContext } from './types'

export const openModalsAtom = atom<Array<ModalState>>([])

export const openModalAtom = atom(
  null,
  <K extends ModalId>(_get: Getter, set: Setter, id: K, context?: ModalContexts[K]) => {
    set(openModalsAtom, (prev: Array<ModalState>) => {
      // Check if modal is already open
      if (prev.some((modal: ModalState) => modal.id === id)) {
        return prev
      }
      return [...prev, { id, context }]
    })
  },
)

export const closeModalAtom = atom(null, (_get, set, id: ModalId) => {
  set(openModalsAtom, (prev: Array<ModalState>) => prev.filter((modal: ModalState) => modal.id !== id))
})

export const closeAllModalsAtom = atom(null, (_get, set) => {
  set(openModalsAtom, [])
})

/**
 * Type for the openModal function with conditional context parameter
 * - If modal requires context: openModal(id, context)
 * - If modal doesn't require context: openModal(id)
 */
type OpenModalFunction = <K extends ModalId>(
  ...args: ModalRequiresContext<K> extends true
    ? [id: K, context: ModalContexts[K]]
    : ModalContexts[K] extends void | undefined
      ? [id: K]
      : [id: K, context?: ModalContexts[K]]
) => void

/**
 * Hook to access modal control functions (openModal, closeModal, closeAllModals)
 * Components using this hook will NOT re-render when modals change
 */
export function useModals() {
  const openModalBase = useSetAtom(openModalAtom)
  const closeModal = useSetAtom(closeModalAtom)
  const closeAllModals = useSetAtom(closeAllModalsAtom)

  // Type the openModal function properly to maintain generic constraints
  const openModal: OpenModalFunction = openModalBase as OpenModalFunction

  return { openModal, closeModal, closeAllModals }
}

/**
 * Hook to access the list of currently open modals
 * Only ModalRenderer should use this hook
 * Components using this hook WILL re-render when modals change
 */
export function useOpenModals() {
  const [modals] = useAtom(openModalsAtom)
  return modals
}

/**
 * Hook to check if a specific modal is open
 * Components using this hook WILL re-render when modals change
 */
export function useIsModalOpen() {
  const [modals] = useAtom(openModalsAtom)
  return (modalId: ModalId) => modals.some((modal) => modal.id === modalId)
}
